import React from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from '@xyflow/react';
import { Check, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useCanvasPalette } from '../../../lib/theme';

export function ConditionEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  data,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const palette = useCanvasPalette();
  const edgeData = data as any;
  const hasError = edgeData?.hasError;
  const strokeColor = hasError
    ? palette.edgeError
    : selected
    ? palette.edgeSelected
    : palette.edge;
  
  // Format the label
  let label = '';
  if (edgeData?.conditions && edgeData.conditions.length > 0) {
    const c = edgeData.conditions[0];
    if (!c.field || c.field === "") {
      label = "New Condition Outfield";
    } else if (c.operator === "is_set") {
      label = `${c.field} is set`;
    } else if (c.operator === "is_not_set") {
      label = `${c.field} is not set`;
    } else {
      label = `${c.field} ${c.operator} ${c.value ?? ""}`;
    }
    
    if (edgeData.conditions.length > 1) {
      label += ` (+${edgeData.conditions.length - 1} more)`;
    }
  }

  let delayLabel = '';
  if (edgeData?.delay && edgeData.delay.value) {
    const val = edgeData.delay.value;
    const unit = edgeData.delay.unit;
    // Capitalize unit for display, e.g. "days" -> "Days"
    const displayUnit = unit.charAt(0).toUpperCase() + unit.slice(1);
    delayLabel = `Wait ${val} ${displayUnit}`;
  }

  return (
    <>
      <BaseEdge 
        path={edgePath} 
        markerEnd={markerEnd || 'url(#xyflow__arrowclosed)'} 
        style={{
          ...style,
          strokeWidth: selected ? 2.5 : 2,
          stroke: strokeColor,
          strokeDasharray: hasError ? '5,5' : 'none',
        }} 
      />
      {(label || delayLabel) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="flex flex-col items-center gap-1.5"
          >
            {delayLabel && (
              <div className={cn(
                "nodrag nopan px-[10px] py-[4px] rounded-[100px] shadow-sm border text-[11px] font-medium whitespace-nowrap inline-flex items-center gap-[6px] transition-colors bg-elevated border-line text-ink-2 hover:border-brand/40",
                hasError && !selected && "border-danger/60 text-danger bg-danger-soft",
                selected && "border-brand text-brand bg-brand-soft"
              )}>
                <Clock className="h-3 w-3 opacity-70" />
                <span>{delayLabel}</span>
              </div>
            )}
            
            {label && (
              <div className={cn(
                "nodrag nopan px-[10px] py-[4px] rounded-[100px] shadow-sm border text-[11px] font-medium whitespace-nowrap inline-flex items-center gap-[6px] transition-colors bg-elevated border-line text-ink-2 hover:border-brand/40",
                hasError && !selected && "border-danger/60 text-danger bg-danger-soft",
                selected && "border-brand text-brand bg-brand-soft"
              )}>
                <Check className="h-3 w-3 opacity-70" />
                <span>{label}</span>
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
